// services/variantLandscapeService.ts

export interface ClinVarVariant {
  position: number;
  proteinChange: string;
  clinicalSignificance: string;
  reviewStatus: string;
  starRating: number;
  conditions: string[];
  variantId: string;
  submissionCount: number;
}

export interface AlphaMissenseScore {
  position: number;
  aminoAcid: string;
  score: number;
  classification: string;
}

export interface ConservationScore {
  position: number;
  phyloP: number | null;
  gerp: number | null;
  phastCons: number | null;
}

export interface FunctionalSite {
  type: 'active_site' | 'binding_site' | 'catalytic_site' | 'domain' | 'motif';
  name: string;
  startPosition: number;
  endPosition: number;
  description: string;
  confidence?: number;
}

export interface VariantLandscapeData {
  geneSymbol: string;
  uniprotId: string;
  proteinLength: number;
  clinvarVariants: ClinVarVariant[];
  alphaMissenseScores: AlphaMissenseScore[];
  conservationScores: ConservationScore[];
  functionalSites: FunctionalSite[];
}

// Mock data generator for development (will be replaced with real API calls)
const generateMockClinVarVariants = (geneSymbol: string, proteinLength: number): ClinVarVariant[] => {
  const variants: ClinVarVariant[] = [];
  const numVariants = Math.floor(proteinLength * 0.05); // ~5% of positions have variants
  
  for (let i = 0; i < numVariants; i++) {
    const position = Math.floor(Math.random() * proteinLength) + 1;
    const significance = Math.random();
    
    let clinicalSignificance: string;
    let starRating: number;
    
    if (significance < 0.3) {
      clinicalSignificance = 'Pathogenic';
      starRating = Math.floor(Math.random() * 4) + 1;
    } else if (significance < 0.5) {
      clinicalSignificance = 'Likely pathogenic';
      starRating = Math.floor(Math.random() * 3) + 1;
    } else if (significance < 0.65) {
      clinicalSignificance = 'Uncertain significance';
      starRating = Math.floor(Math.random() * 2) + 1;
    } else if (significance < 0.85) {
      clinicalSignificance = 'Likely benign';
      starRating = Math.floor(Math.random() * 3) + 1;
    } else {
      clinicalSignificance = 'Benign';
      starRating = Math.floor(Math.random() * 4) + 1;
    }
    
    variants.push({
      position,
      proteinChange: `p.${['Ala', 'Arg', 'Asn', 'Asp', 'Cys', 'Gln', 'Glu', 'Gly', 'His', 'Ile', 'Leu', 'Lys', 'Met', 'Phe', 'Pro', 'Ser', 'Thr', 'Trp', 'Tyr', 'Val'][Math.floor(Math.random() * 20)]}${position}${['Ala', 'Arg', 'Asn', 'Asp', 'Cys', 'Gln', 'Glu', 'Gly', 'His', 'Ile', 'Leu', 'Lys', 'Met', 'Phe', 'Pro', 'Ser', 'Thr', 'Trp', 'Tyr', 'Val'][Math.floor(Math.random() * 20)]}`,
      clinicalSignificance,
      reviewStatus: ['no assertion criteria provided', 'criteria provided, single submitter', 'criteria provided, multiple submitters, no conflicts', 'reviewed by expert panel', 'practice guideline'][Math.floor(Math.random() * 5)],
      starRating,
      conditions: [`${geneSymbol}-related disorder`],
      variantId: `VCV${Math.floor(Math.random() * 1000000)}`,
      submissionCount: Math.floor(Math.random() * 10) + 1,
    });
  }
  
  return variants;
};

const generateMockAlphaMissenseScores = (proteinLength: number): AlphaMissenseScore[] => {
  const scores: AlphaMissenseScore[] = [];
  
  for (let i = 1; i <= proteinLength; i++) {
    const score = Math.random();
    let classification: string;
    
    if (score >= 0.564) {
      classification = 'pathogenic';
    } else if (score <= 0.34) {
      classification = 'benign';
    } else {
      classification = 'ambiguous';
    }
    
    scores.push({
      position: i,
      aminoAcid: ['A', 'R', 'N', 'D', 'C', 'Q', 'E', 'G', 'H', 'I', 'L', 'K', 'M', 'F', 'P', 'S', 'T', 'W', 'Y', 'V'][Math.floor(Math.random() * 20)],
      score,
      classification,
    });
  }
  
  return scores;
};

const generateMockConservationScores = (proteinLength: number): ConservationScore[] => {
  const scores: ConservationScore[] = [];
  
  for (let i = 1; i <= proteinLength; i++) {
    scores.push({
      position: i,
      phyloP: Math.random() * 10 - 2, // -2 to 8 range
      gerp: Math.random() * 6, // 0 to 6 range
      phastCons: Math.random(), // 0 to 1 range
    });
  }
  
  return scores;
};

const generateMockFunctionalSites = (geneSymbol: string, proteinLength: number): FunctionalSite[] => {
  const sites: FunctionalSite[] = [];
  
  // Generate domains
  const numDomains = Math.floor(Math.random() * 3) + 1;
  for (let i = 0; i < numDomains; i++) {
    const start = Math.floor(Math.random() * (proteinLength * 0.8));
    const length = Math.floor(Math.random() * 100) + 50;
    sites.push({
      type: 'domain',
      name: `Domain ${i + 1}`,
      startPosition: start,
      endPosition: Math.min(start + length, proteinLength),
      description: `Functional domain of ${geneSymbol}`,
      confidence: Math.random() * 0.5 + 0.5,
    });
  }
  
  // Generate active sites
  const numActiveSites = Math.floor(Math.random() * 5) + 1;
  for (let i = 0; i < numActiveSites; i++) {
    const position = Math.floor(Math.random() * proteinLength) + 1;
    sites.push({
      type: 'active_site',
      name: `Active Site ${i + 1}`,
      startPosition: position,
      endPosition: position + 2,
      description: 'Catalytic active site',
      confidence: Math.random() * 0.3 + 0.7,
    });
  }
  
  // Generate binding sites
  const numBindingSites = Math.floor(Math.random() * 3) + 1;
  for (let i = 0; i < numBindingSites; i++) {
    const position = Math.floor(Math.random() * proteinLength) + 1;
    sites.push({
      type: 'binding_site',
      name: `Binding Site ${i + 1}`,
      startPosition: position,
      endPosition: position + 5,
      description: 'Ligand binding site',
      confidence: Math.random() * 0.4 + 0.6,
    });
  }
  
  return sites;
};

// Main function to fetch variant landscape data
export const fetchVariantLandscape = async (geneSymbol: string, uniprotId?: string, proteinLength?: number): Promise<VariantLandscapeData | null> => {
  try {
    console.log(`[VariantLandscape] Fetching data for ${geneSymbol} (${uniprotId})`);
    
    // Use provided protein length or generate a reasonable default
    const length = proteinLength || 500;
    
    const landscapeData: VariantLandscapeData = {
      geneSymbol,
      uniprotId: uniprotId || 'MOCK_ID',
      proteinLength: length,
      clinvarVariants: generateMockClinVarVariants(geneSymbol, length),
      alphaMissenseScores: generateMockAlphaMissenseScores(length),
      conservationScores: generateMockConservationScores(length),
      functionalSites: generateMockFunctionalSites(geneSymbol, length),
    };
    
    console.log(`[VariantLandscape] Generated ${landscapeData.clinvarVariants.length} variants for ${length} residues`);
    return landscapeData;
  } catch (error) {
    console.error('Error fetching variant landscape data:', error);
    return null;
  }
};

// Calculate pathogenicity heatmap for 3D surface
export const calculatePathogenicityHeatmap = (
  landscapeData: VariantLandscapeData,
  spatialPositions: { position: number; coords: [number, number, number] }[],
  radius: number = 10.0
): Map<number, number> => {
  const heatmap = new Map<number, number>();
  
  spatialPositions.forEach(({ position, coords }) => {
    let pathogenicityScore = 0;
    
    // Base score from AlphaMissense
    const alphaMissense = landscapeData.alphaMissenseScores.find(s => s.position === position);
    if (alphaMissense) {
      pathogenicityScore += alphaMissense.score * 0.4;
    }
    
    // Conservation score contribution
    const conservation = landscapeData.conservationScores.find(s => s.position === position);
    if (conservation) {
      if (conservation.phyloP !== null) {
        pathogenicityScore += Math.max(0, conservation.phyloP / 8) * 0.3;
      }
      if (conservation.gerp !== null) {
        pathogenicityScore += (conservation.gerp / 6) * 0.2;
      }
    }
    
    // Nearby pathogenic variant density
    let pathogenicDensity = 0;
    spatialPositions.forEach(({ position: otherPos, coords: otherCoords }) => {
      if (position === otherPos) return;
      
      const distance = Math.sqrt(
        Math.pow(coords[0] - otherCoords[0], 2) +
        Math.pow(coords[1] - otherCoords[1], 2) +
        Math.pow(coords[2] - otherCoords[2], 2)
      );
      
      if (distance <= radius) {
        const variant = landscapeData.clinvarVariants.find(v => v.position === otherPos);
        if (variant && (variant.clinicalSignificance.toLowerCase().includes('pathogenic') || variant.clinicalSignificance.toLowerCase().includes('likely pathogenic'))) {
          pathogenicDensity += (variant.starRating / 4) * (1 - distance / radius);
        }
      }
    });
    
    pathogenicityScore += pathogenicDensity * 0.1;
    
    heatmap.set(position, Math.min(1, pathogenicityScore));
  });
  
  return heatmap;
};

// Get variants within spatial radius
export const getVariantsInRadius = (
  landscapeData: VariantLandscapeData,
  centerPosition: number,
  spatialPositions: { position: number; coords: [number, number, number] }[],
  radius: number = 8.0
): ClinVarVariant[] => {
  const centerCoords = spatialPositions.find(p => p.position === centerPosition)?.coords;
  if (!centerCoords) return [];
  
  const nearbyPositions = spatialPositions.filter(({ position, coords }) => {
    if (position === centerPosition) return false;
    
    const distance = Math.sqrt(
      Math.pow(centerCoords[0] - coords[0], 2) +
      Math.pow(centerCoords[1] - coords[1], 2) +
      Math.pow(centerCoords[2] - coords[2], 2)
    );
    
    return distance <= radius;
  }).map(p => p.position);
  
  return landscapeData.clinvarVariants.filter(v => nearbyPositions.includes(v.position));
};

// Check if position is in functional site
export const isInFunctionalSite = (position: number, functionalSites: FunctionalSite[]): FunctionalSite | null => {
  return functionalSites.find(site => 
    position >= site.startPosition && position <= site.endPosition
  ) || null;
};